import React from "react";
import PropTypes from "prop-types";

/**
 * Detailed information about each image generation step
 * Aligned with backend status progression from updateBookWithPreviewOne and updateBookWithPreviewTwo
 */
const generationStepInfo = {
  pending:{
    title: "Preparing...",
    description: "Getting ready to generate your book",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  received:{
    title: "Request Received",
    description: "Processing your request...",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  initializing:{
    title: "Initializing...",
    description: "Setting up image generation...",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  textElementsCreated:{
    title: "Text Elements Created",
    description: "Story text has been added to your book",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    )
  },
  generatingCharacter: {
    title: "Generating World Step",
    description: "Creating characters, places, and objects for your story",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  },
  generatingImagePrompts: {
    title: "Generating Image Prompts",
    description: "Creating prompts for image generation...",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    )
  },
  generatingCoverImage: {
    title: "Generating Cover Image",
    description: "Creating your book cover...",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    )
  },
  updatingImageElements: {
    title: "Adding Image Elements",
    description: "Updating your book with images...",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )
  }
};

/**
 * Component showing current step in the image generation process
 */
export default function ImageGenerationSteps({ currentStep, progress }) {
  // Any status with copy in generationStepInfo is shown as-is. This used to be
  // gated on a shorter list, so statuses ImageLoadingPlaceholder derives from
  // progress (e.g. generatingCoverImage at 56-75%) fell through to the
  // "Generating World Step" copy.
  const activeStep = generationStepInfo[currentStep] ? currentStep : "generatingCharacter";
  
  // Get the step info for current step
  const stepInfo = generationStepInfo[activeStep];
  
  // Debug logging
  console.log('[ImageGenerationSteps] currentStep:', currentStep, 'activeStep:', activeStep, 'stepInfo:', stepInfo);


  return (
    <div className="p-3 bg-white bg-opacity-80 rounded-lg shadow-sm">
      <h3 className="text-xs font-medium text-gray-500 mb-3">COMPLETING DESIGN ELEMENTS</h3>
      
      {/* Current step info */}
      <div className="flex flex-col items-center justify-center text-center">
        <div>
          <h4 className="text-base font-semibold text-gray-800">{stepInfo.title}</h4>
          <p className="text-sm text-gray-500">{stepInfo.description}</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-3 w-full bg-gray-200 rounded-full h-1.5">
        <div 
          className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${Math.min(100, Math.max(0, progress || 0))}%` }}
        />
      </div>
    </div>
  );
}

ImageGenerationSteps.propTypes = {
  currentStep: PropTypes.oneOf(Object.keys(generationStepInfo)),
  progress: PropTypes.number
};
